import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useBack } from '../hooks';
import { C } from '../styles/common';
import MeasuredJobs from './MeasuredJobs';
import Routines from './Routines';

// Admin → Measuring: who measures what (designed with the director 22 Sep and
// 7 Oct 2026; the "Measuring Recorder" prototype). The app proposes
// measurements from the board; the director gives each one to a person, says
// "not today" with a reason, or leaves it open -- open asks nobody. He can
// also ask for a measurement the app did not propose.

const TEAL = C.teal, AMBER = '#f59e0b', GREEN = '#34d399', DIM = '#9ca3af', SUNK = '#12161b';
const ICON = { mould_change: 'fa-right-left', cycle: 'fa-stopwatch', warmup: 'fa-temperature-half', packing: 'fa-box', purge: 'fa-droplet', cleaning: 'fa-broom', attention: 'fa-person', assembly: 'fa-screwdriver-wrench', routine: 'fa-list-ol' };
const EFFORT = {
  mould_change: (t) => (t.mould_off_id ? '4 taps across the change' : '3 taps'),
  cycle: () => 'Type one number · 10 seconds',
  warmup: () => '2 taps at the start of the shift',
  packing: () => 'Tap each full box, alongside the work',
  purge: () => '2 taps across the purge',
  cleaning: () => '2 taps across the cleaning',
  attention: () => 'Tap in and out for an hour',
  assembly: (t) => (t.method === 'steps' ? 'Step by step: a tap as each next step starts' : t.step ? 'A batch of this step: start, finish, how many' : 'A batch: start, finish, how many and how many people'),
  routine: () => 'A tap as each next step starts; steps named at the end',
};
const KIND = { cycle: 'Cycle time', packing: 'Packing', warmup: 'Warm-up', mould_change: 'Mould change', purge: 'Colour purge', cleaning: 'Cleaning', attention: 'Time at the machine', assembly: 'Assembly', routine: 'Routine' };
// What can be asked for, and what each is asked of.
const ASK = [['cycle', 'part'], ['packing', 'part'], ['attention', 'part'], ['warmup', 'machine'], ['cleaning', 'machine'], ['purge', 'machine'], ['assembly', 'product'], ['routine', 'routine']];
const askOf = (kind) => (ASK.find(a => a[0] === kind) || ASK[0])[1];
const WHY = ['New person: speed check', 'After a process change', 'Check against earlier recordings'];
const dayLabel = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

function headline(t) {
  const m = t.measurement;
  if (!m || m.value == null) return '';
  const r = m.result || {};
  if (t.kind === 'mould_change') return `${m.value} min` + (r.on_min != null ? ` (${[r.off_min, r.on_min, r.startup_min].filter(v => v != null).join(' + ')})` : '');
  if (t.kind === 'packing') return `${r.box_s} s a box` + (r.pallet_min ? ` · ${r.pallet_min} min a pallet` : '');
  if (t.kind === 'attention') return `${m.value} min an hour · level ${r.level}`;
  if (t.kind === 'assembly' || t.kind === 'routine') return `${m.value} s each · ${r.count} by ${r.people}`;
  return `${m.value} ${m.unit}`;
}

export default function Measuring() {
  const goBack = useBack('/admin');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = ['measured', 'routines'].includes(params.get('tab')) ? params.get('tab') : 'who';
  const setTab = (k) => setParams(k === 'who' ? {} : { tab: k });
  const [data, setData] = useState(null);
  const [day, setDay] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [onMachine, setOnMachine] = useState({});     // per day and machine: who works there (a helper, not stored)
  const [picking, setPicking] = useState(null);       // the measurement being held back
  const [person, setPerson] = useState(null);         // the person whose day is open
  const [ask, setAsk] = useState(null);
  const [guide, setGuide] = useState(() => { try { return localStorage.getItem('mouldos.measuring.guide') !== 'hidden'; } catch { return true; } });

  const load = useCallback((d) => {
    api.get('/api/v1/measuring/plan', { params: d ? { day: d } : {} })
      .then(r => { setData(r.data); setDay(r.data.day); setError(''); })
      .catch(err => setError(err.response?.data?.error || 'The measuring plan could not be read.'));
  }, []);
  useEffect(() => { if (tab === 'who') load(null); }, [load, tab]);

  async function act(fn) {
    setBusy(true); setError('');
    try { await fn(); load(day); }
    catch (err) { setError(err.response?.data?.error || 'That did not work. Try again.'); }
    setBusy(false);
  }
  const assign = (ids, userId) => act(() => api.post('/api/v1/measuring/tasks/assign', { ids, user_id: userId || null }));
  const notToday = (id, reason, note) => act(async () => { await api.post(`/api/v1/measuring/tasks/${id}/not-today`, { reason, note }); setPicking(null); });
  const reopen = (id) => act(() => api.post(`/api/v1/measuring/tasks/${id}/reopen`));
  const hideGuide = (v) => { setGuide(v); try { localStorage.setItem('mouldos.measuring.guide', v ? 'shown' : 'hidden'); } catch { /* fine */ } };

  if (tab === 'measured') return <Page goBack={goBack} tab={tab} setTab={setTab}><MeasuredJobs onGoToGiving={() => setTab('who')} /></Page>;
  if (tab === 'routines') return <Page goBack={goBack} tab={tab} setTab={setTab}><Routines /></Page>;
  if (!data) return <Page goBack={goBack} tab={tab} setTab={setTab}>{error ? <Note tone={AMBER}>{error}</Note> : <div style={{ color: C.muted }}>Loading…</div>}</Page>;

  const tasks = data.tasks || [];
  const people = data.people || [];
  const name = (id) => (people.find(p => p.id === id) || {}).name || '';
  const count = { all: tasks.length, assigned: 0, not_today: 0, open: 0, done: 0 };
  tasks.forEach(t => { count[t.status === 'done' ? 'assigned' : t.status] = (count[t.status === 'done' ? 'assigned' : t.status] || 0) + 1; if (t.status === 'done') count.done++; });
  const floor = people.filter(p => p.level !== 'director');

  // Every machine on the board that day, and any place with a measurement asked by hand.
  const places = (data.board || []).map(b => ({ ...b }));
  tasks.forEach(t => { if (t.machine_id && !places.some(p => p.machine_id === t.machine_id)) places.push({ machine_id: t.machine_id, machine: t.machine, parts: [] }); });
  // What is not at a machine: assembly, and routines of nothing in particular.
  if (tasks.some(t => !t.machine_id)) places.push({ machine_id: null, machine: 'At the table', parts: [] });

  return (
    <Page goBack={goBack} tab={tab} setTab={setTab}>
      {guide ? (
        <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px 16px', marginBottom: 16 }}>
          {[['1', 'Say who is where.', 'On each machine, choose who works there that day.'],
            ['2', 'Give out the measuring.', 'One click gives that person everything open on their machine. Or choose per measurement, or open a person below and give them a whole kind.'],
            ['3', 'Hold back what does not fit.', 'Not today where it would disrupt the flow or the day is not representative. Left open, nobody is asked.'],
            ['+', 'Ask for something else.', 'A measurement the app did not propose, such as a new person\'s speed check.']].map(([n, b, t]) => (
            <div key={n} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 8, fontSize: 13, color: DIM }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: C.tealDim, color: TEAL, display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12 }}>{n}</span>
              <div><b style={{ color: C.text }}>{b}</b> {t}</div>
            </div>
          ))}
          <button onClick={() => hideGuide(false)} style={{ ...link, justifySelf: 'start' }}>Hide this</button>
        </div>
      ) : (
        <button onClick={() => hideGuide(true)} style={{ ...link, marginBottom: 12 }}>How giving out measuring works</button>
      )}

      {error && <Note tone={AMBER}>{error}</Note>}
      {data.sync_error && (
        <Note tone={AMBER}>
          The proposals could not be brought in step with the board just now, so this may be out of date. The app tries again in a minute; reload the page then. ({data.sync_error})
        </Note>
      )}
      {!floor.length && (
        <Note tone={AMBER}>
          Nobody on the floor has a login yet, so only directors can be given measuring.{' '}
          <button onClick={() => navigate('/admin/users')} style={{ ...link, color: TEAL }}>Add the team on the Users page</button>
        </Note>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }} role="group" aria-label="Day">
        {data.days.map(d => (
          <button key={d.day} onClick={() => { setPerson(null); setPicking(null); load(d.day); }} aria-pressed={d.day === day}
            style={{ ...chipBtn, borderColor: d.day === day ? TEAL : C.border, color: d.day === day ? TEAL : DIM, background: d.day === day ? C.tealDim : C.surface }}>
            {dayLabel(d.day)}{d.day === data.today ? ' · today' : ''}<span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: C.muted, marginLeft: 6 }}>{d.all}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <Chip label="Measurements" n={count.all} />
        <Chip label="Given to someone" n={count.assigned} tone={TEAL} />
        <Chip label="Not today" n={count.not_today} tone={AMBER} />
        <Chip label="Nobody asked" n={count.open} />
        <Chip label="Done" n={count.done} tone={GREEN} />
        {!ask && <button onClick={() => setAsk({ day, kind: 'cycle', subject: '', who: person || '', note: '' })} style={{ ...btn, ...teal, marginLeft: 'auto' }}>+ Ask for a measurement</button>}
      </div>

      {ask && <AskForm ask={ask} setAsk={setAsk} data={data} people={people} busy={busy}
        onSubmit={(body) => act(async () => { await api.post('/api/v1/measuring/tasks', body); setAsk(null); if (body.day !== day) setDay(body.day); })} />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: person ? 8 : 16 }}>
        {people.map(p => {
          const n = tasks.filter(t => t.assigned_to === p.id && ['assigned', 'done'].includes(t.status)).length;
          return (
            <button key={p.id} onClick={() => setPerson(person === p.id ? null : p.id)} aria-pressed={person === p.id}
              style={{ ...card, padding: '7px 11px', display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', color: C.text, borderColor: person === p.id ? TEAL : C.border, background: person === p.id ? C.tealDim : C.surface }}>
              <span style={{ width: 26, height: 26, borderRadius: '50%', background: SUNK, border: `1px solid ${C.border}`, display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 12, color: TEAL }}>{p.name[0]}</span>
              <span style={{ textAlign: 'left' }}><div style={{ fontSize: 13 }}>{p.name}</div><div style={{ fontSize: 11.5, color: C.muted, textTransform: 'capitalize' }}>{p.level}</div></span>
              <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: DIM, marginLeft: 4 }}>{n} that day</span>
            </button>
          );
        })}
      </div>
      {person && <PersonDay pid={person} name={name(person)} tasks={tasks} busy={busy} assign={assign} />}

      {!places.length && <div style={{ ...card, color: C.muted }}>Nothing is on the board on {dayLabel(day)}, so there is nothing to measure.</div>}
      {places.map(pl => {
        const here = tasks.filter(t => (t.machine_id || null) === pl.machine_id);
        const key = `${day}|${pl.machine_id}`;
        // Who is on it: what was chosen here, else whoever already has most of it.
        const tally = {};
        here.forEach(t => { if (t.assigned_to) tally[t.assigned_to] = (tally[t.assigned_to] || 0) + 1; });
        const guess = Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0] || '';
        const on = onMachine[key] !== undefined ? onMachine[key] : guess;
        const open = here.filter(t => t.status === 'open');
        return (
          <section key={pl.machine_id} style={{ ...card, padding: 0, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', alignItems: 'center', padding: '12px 14px', background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 800, fontSize: 15 }}>{pl.machine}</span>
              <span style={{ color: DIM, fontSize: 13 }}>{(pl.parts || []).map(p => `${p.part || 'Run'} · ${Number(p.qty).toLocaleString('en-GB')} pcs`).join(' · ')}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12.5, color: DIM }}>
                <label htmlFor={'on-' + pl.machine_id}>{pl.machine_id ? 'On this machine' : 'Who is there'}</label>
                <select id={'on-' + pl.machine_id} value={on} onChange={e => setOnMachine(o => ({ ...o, [key]: e.target.value }))} style={select}>
                  <option value="">nobody set</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {on && open.length > 0 && (
                  <button disabled={busy} onClick={() => assign(open.map(t => t.id), on)} style={{ ...btn, ...teal }}>
                    Give {name(on)} the {open.length} open here
                  </button>
                )}
              </span>
            </div>
            {!here.length && <div style={{ padding: '10px 14px', color: C.muted, fontSize: 13 }}>Nothing to measure here that day.</div>}
            {here.map(t => (
              <TaskRow key={t.id} t={t} people={people} busy={busy} picking={picking === t.id} reasons={data.reasons}
                onAssign={(uid) => assign([t.id], uid)} onPick={() => setPicking(picking === t.id ? null : t.id)}
                onNotToday={(r, n) => notToday(t.id, r, n)} onReopen={() => reopen(t.id)}
                onHow={(body) => act(() => api.post(`/api/v1/measuring/tasks/${t.id}/how`, body))} />
            ))}
          </section>
        );
      })}
    </Page>
  );
}

function TaskRow({ t, people, busy, picking, reasons, onAssign, onPick, onNotToday, onReopen, onHow }) {
  const [note, setNote] = useState('');
  const skipped = t.status === 'not_today';
  const running = t.measurement && t.measurement.status === 'running';
  return (
    <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'grid', gridTemplateColumns: '34px minmax(0,1fr) auto', gap: '10px 12px', alignItems: 'start' }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', fontSize: 14,
        background: t.kind === 'packing' ? 'rgba(245,158,11,0.13)' : C.tealDim, color: t.kind === 'packing' ? AMBER : TEAL }}><i className={`fa-solid ${ICON[t.kind] || 'fa-circle'}`} aria-hidden="true" /></span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: skipped ? C.muted : C.text }}>
          {t.title}
          {t.source === 'asked' && <span style={{ fontSize: 10.5, fontWeight: 700, color: TEAL, border: '1px solid rgba(20,184,166,0.5)', borderRadius: 4, padding: '1px 6px', marginLeft: 6 }}>Asked by you</span>}
        </div>
        <div style={{ color: skipped ? C.muted : DIM, fontSize: 12.5, marginTop: 2 }}>{t.why}</div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>{(EFFORT[t.kind] || (() => ''))(t)}</div>
        {t.kind === 'assembly' && !skipped && t.status !== 'done' && !running && <AssemblyHow t={t} busy={busy} onHow={onHow} />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', minWidth: 170 }}>
        {t.status === 'done' ? (
          <><span style={{ fontSize: 12, fontWeight: 700, color: GREEN }}>Done{t.assigned_name ? ` by ${t.assigned_name}` : ''}</span><span style={{ fontSize: 12, color: DIM }}>{headline(t)}</span></>
        ) : running ? (
          <span style={{ fontSize: 12, fontWeight: 700, color: TEAL }}>{t.measurement.recorded_by_name || t.assigned_name} is measuring</span>
        ) : skipped ? (
          <>
            <span style={{ fontSize: 12, fontWeight: 700, color: AMBER }}>Not today</span>
            <span style={{ fontSize: 12, color: DIM, textAlign: 'right' }}>{t.not_today_reason}{t.not_today_note ? `: ${t.not_today_note}` : ''}</span>
            <button disabled={busy} onClick={onReopen} style={{ ...btn, background: 'none', color: DIM }}>Undo</button>
          </>
        ) : (
          <>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <label htmlFor={'who-' + t.id} style={{ fontSize: 12, color: DIM }}>Who measures</label>
              <select id={'who-' + t.id} disabled={busy} value={t.status === 'assigned' ? t.assigned_to : ''} onChange={e => onAssign(e.target.value)} style={select}>
                <option value="">nobody asked</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </span>
            <button disabled={busy} onClick={onPick} style={{ ...btn, background: 'none', color: AMBER, borderColor: 'rgba(245,158,11,0.5)' }}>Not today</button>
            <span style={{ fontSize: 12, fontWeight: 700, color: t.status === 'assigned' ? TEAL : C.muted }}>{t.status === 'assigned' ? `Asked: ${t.assigned_name || ''}` : 'Proposed'}</span>
          </>
        )}
      </div>
      {picking && (
        <div style={{ gridColumn: '2 / -1', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', background: SUNK, border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 10px' }}>
          <span style={{ fontSize: 12, color: DIM, width: '100%' }}>Why not today? The board keeps its guess, and the app proposes it again next time.</span>
          {reasons.map(r => (
            <button key={r} disabled={busy || (r === 'Other' && !note.trim())} onClick={() => onNotToday(r, note)}
              style={{ ...btn, background: 'none', borderRadius: 99, fontWeight: 500 }}>{r}</button>
          ))}
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="A note (needed for Other)" style={{ ...select, flex: '1 1 180px' }} />
          <button onClick={onPick} style={{ ...btn, background: 'none', color: DIM }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// Assembly: how, chosen per job (the director, 7 Oct 2026): a batch for a quick
// high-level view or a new person; step by step to see what to improve. One
// step alone is a batch of that step.
function AssemblyHow({ t, busy, onHow }) {
  const steps = (t.plan && t.plan.steps) || [];
  const seg = (m, label) => {
    const on = !t.stock_item_id && (t.method || 'batch') === m;
    return (
      <button key={m} disabled={busy} onClick={() => onHow({ method: m, stock_item_id: null })} aria-pressed={on}
        style={{ background: on ? C.tealDim : 'none', border: `1px solid ${on ? TEAL : C.border}`, color: on ? TEAL : DIM, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 7 }}>{label}</button>
    );
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 7 }}>
      <span style={{ fontSize: 12, color: DIM }}>How</span>
      {seg('batch', 'Batch')}
      {steps.length > 1 && seg('steps', 'Step by step')}
      {steps.length > 1 && (
        <select aria-label="One step only" disabled={busy} value={t.stock_item_id || ''} onChange={e => onHow(e.target.value ? { method: 'batch', stock_item_id: Number(e.target.value) } : { method: 'batch', stock_item_id: null })} style={{ ...select, fontSize: 12, padding: '4px 6px', maxWidth: 220 }}>
          <option value="">The whole product</option>
          {steps.map(s => <option key={s.item_id} value={s.item_id}>Only: {s.name}</option>)}
        </select>
      )}
      {!steps.length && <span style={{ fontSize: 12, color: C.muted }}>No steps yet: they are set in Stock → Build steps.</span>}
    </div>
  );
}

function PersonDay({ pid, name, tasks, busy, assign }) {
  const mine = tasks.filter(t => t.assigned_to === pid && ['assigned', 'done'].includes(t.status));
  const kinds = {};
  tasks.filter(t => t.status === 'open').forEach(t => { (kinds[t.kind] = kinds[t.kind] || []).push(t.id); });
  return (
    <div style={{ background: SUNK, border: `1px solid ${TEAL}`, borderRadius: 12, padding: '12px 14px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 14 }}><b>{name}</b> · {mine.length} measurement{mine.length === 1 ? '' : 's'} that day</div>
      {!mine.length && <div style={{ fontSize: 12.5, color: C.muted }}>Nothing given to {name} yet.</div>}
      {mine.map(t => (
        <div key={t.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', fontSize: 13 }}>
          <span style={{ flex: 1, minWidth: 0 }}>{t.title}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.status === 'done' ? GREEN : TEAL }}>{t.status === 'done' ? 'Done' : 'Asked'}</span>
          {t.status === 'assigned' && <button disabled={busy} onClick={() => assign([t.id], null)} style={{ ...btn, background: 'none', color: DIM }}>Take back</button>}
        </div>
      ))}
      {Object.keys(kinds).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', paddingTop: 6, borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 12, color: C.muted, marginRight: 4 }}>Give {name} every open</span>
          {Object.entries(kinds).map(([k, ids]) => (
            <button key={k} disabled={busy} onClick={() => assign(ids, pid)} style={btn}>{KIND[k] || k} · {ids.length}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function AskForm({ ask, setAsk, data, people, busy, onSubmit }) {
  const board = ask.day === data.day ? data.board || [] : [];
  const of = askOf(ask.kind);
  const needsBoard = of === 'part' || of === 'machine';
  // Products and routines are not on the board: read once the form is open.
  const [known, setKnown] = useState(null);
  useEffect(() => {
    api.get('/api/v1/measuring/subjects').then(r => setKnown(r.data)).catch(() => setKnown({ products: [], routines: [] }));
  }, []);
  // What can be asked of for this kind that day.
  const subjects = of === 'machine'
    ? board.map(b => ({ v: String(b.machine_id), l: b.machine }))
    : of === 'product' ? ((known && known.products) || []).map(p => ({ v: String(p.id), l: p.name }))
    : of === 'routine' ? ((known && known.routines) || []).map(r => ({ v: String(r.id), l: r.name }))
    : board.flatMap(b => (b.parts || []).filter(p => p.part_id).map(p => ({ v: String(p.part_id), l: `${p.part} · ${b.machine}` })));
  const subject = subjects.some(s => s.v === ask.subject) ? ask.subject : (subjects[0] ? subjects[0].v : '');
  const set = (k, v) => setAsk(a => ({ ...a, [k]: v }));
  const steps = of === 'product' ? (((known && known.products) || []).find(p => String(p.id) === subject) || {}).steps || [] : [];
  const subjectBody = of === 'machine' ? { machine_id: Number(subject) } : of === 'product' ? { product_id: subject, method: ask.how || 'batch', stock_item_id: ask.step ? Number(ask.step) : null }
    : of === 'routine' ? { routine_id: Number(subject) } : { part_id: Number(subject) };
  return (
    <div style={{ background: SUNK, border: `1px solid ${TEAL}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 10 }}>Ask for a measurement</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px 14px' }}>
        <Field label="Day" id="ask-day">
          <select id="ask-day" value={ask.day} onChange={e => set('day', e.target.value)} style={{ ...select, width: '100%' }}>
            {data.days.map(d => <option key={d.day} value={d.day}>{dayLabel(d.day)}</option>)}
          </select>
        </Field>
        <Field label="What" id="ask-kind">
          <select id="ask-kind" value={ask.kind} onChange={e => set('kind', e.target.value)} style={{ ...select, width: '100%' }}>
            {ASK.map(([k]) => <option key={k} value={k}>{KIND[k]}</option>)}
          </select>
        </Field>
        <Field label={{ machine: 'Machine', product: 'Product', routine: 'Routine', part: 'Part' }[of]} id="ask-subject">
          {needsBoard && ask.day !== data.day
            ? <span style={{ fontSize: 12, color: C.muted }}>Open that day first to see its board.</span>
            : subjects.length
              ? <select id="ask-subject" value={subject} onChange={e => setAsk(a => ({ ...a, subject: e.target.value, step: '' }))} style={{ ...select, width: '100%' }}>
                  {subjects.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              : <span style={{ fontSize: 12, color: C.muted }}>{of === 'routine' ? 'No routine yet: make one on the Routines tab.' : of === 'product' ? 'Reading…' : 'Nothing on the board that day.'}</span>}
        </Field>
        {of === 'product' && (
          <Field label="How" id="ask-how">
            <select id="ask-how" value={ask.step ? `step:${ask.step}` : (ask.how || 'batch')} style={{ ...select, width: '100%' }}
              onChange={e => setAsk(a => (e.target.value.startsWith('step:') ? { ...a, how: 'batch', step: e.target.value.slice(5) } : { ...a, how: e.target.value, step: '' }))}>
              <option value="batch">A batch, the whole product</option>
              {steps.length > 1 && <option value="steps">Step by step</option>}
              {steps.length > 1 && steps.map(s => <option key={s.item_id} value={`step:${s.item_id}`}>Only: {s.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Who measures" id="ask-who">
          <select id="ask-who" value={ask.who} onChange={e => set('who', e.target.value)} style={{ ...select, width: '100%' }}>
            <option value="">nobody yet</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </div>
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label htmlFor="ask-note" style={{ fontSize: 12, color: DIM, fontWeight: 600 }}>Why · they see this on their phone</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {WHY.map(w => <button key={w} onClick={() => set('note', w)} style={{ ...btn, background: 'none', borderRadius: 99, fontWeight: 500, color: ask.note === w ? TEAL : C.text, borderColor: ask.note === w ? TEAL : C.border }}>{w}</button>)}
        </div>
        <input id="ask-note" value={ask.note} onChange={e => set('note', e.target.value)} placeholder="Or write your own (optional)" style={select} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button disabled={busy || (needsBoard && ask.day !== data.day) || !subject}
          onClick={() => onSubmit({ day: ask.day, kind: ask.kind, ...subjectBody, user_id: ask.who || null, note: ask.note })}
          style={{ ...btn, ...teal }}>{ask.who ? `Ask ${(people.find(p => p.id === ask.who) || {}).name}` : 'Add it, nobody asked yet'}</button>
        <button onClick={() => setAsk(null)} style={{ ...btn, background: 'none', color: DIM }}>Cancel</button>
      </div>
    </div>
  );
}

const TABS = [
  ['who', 'Who measures what', 'The app proposes measurements from the board; you give each one to a person, or say not today. Anything left open asks nobody.'],
  ['measured', 'Measured jobs', 'What was recorded, next to the value the app uses now. Apply takes the middle of the recordings into the plan; Keep leaves the old value, with a reason. Both can be undone.'],
  ['routines', 'Routines', 'A process recorded as laps: tap, tap, tap, then name them. You confirm the steps, and from then on they are the buttons. Every change is a new version, in History.'],
];

function Page({ goBack, tab, setTab, children }) {
  return (
    <div style={{ padding: '20px 16px 60px', fontFamily: 'system-ui, sans-serif', color: C.text, background: C.bg, minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <button onClick={goBack} style={{ ...link, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: 11 }} /> Back
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 10px' }}>Measuring</h1>
        <div role="tablist" aria-label="Measuring" style={{ display: 'flex', gap: 18, borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
          {TABS.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              style={{ background: 'none', border: 'none', borderBottom: `2px solid ${tab === k ? TEAL : 'transparent'}`, color: tab === k ? C.text : C.muted,
                       fontWeight: 700, fontSize: 14, padding: '8px 2px', marginBottom: -1, cursor: 'pointer' }}>{label}</button>
          ))}
        </div>
        <p style={{ color: C.muted, fontSize: 14, margin: '0 0 18px', maxWidth: '70ch' }}>
          {(TABS.find(t => t[0] === tab) || TABS[0])[2]}
        </p>
        {children}
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}><label htmlFor={id} style={{ fontSize: 12, color: DIM, fontWeight: 600 }}>{label}</label>{children}</div>;
}
function Chip({ label, n, tone }) {
  return <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '4px 10px', borderRadius: 99, border: `1px solid ${C.border}`, fontSize: 12.5, color: DIM, background: C.surface }}>{label} <b style={{ color: tone || C.text }}>{n}</b></span>;
}
function Note({ tone, children }) {
  return <div style={{ background: SUNK, border: `1px solid ${tone}`, borderRadius: 9, padding: '10px 12px', fontSize: 13, color: C.text, marginBottom: 14 }}>{children}</div>;
}

const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px' };
const btn = { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 10px', color: C.text, fontWeight: 600, fontSize: 12.5, cursor: 'pointer' };
const teal = { background: TEAL, borderColor: TEAL, color: '#0b1215' };
const chipBtn = { border: '1px solid', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 13 };
const select = { background: SUNK, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 8px', color: C.text, fontSize: 13 };
const link = { background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' };
